import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight, MapPin } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { Motif } from "./Motif";
import { Button } from "./Button";
import { FAQ_ITEMS, scrollToId } from "../../data/site";
import { useCookieConsent } from "./cookieConsent";

const FaqRow = ({ item, index, isOpen, onToggle }) => {
  const open = isOpen;
  return (
    <Reveal delay={index * 0.06}>
      <div
        data-testid={`faq-item-${index}`}
        className="bg-white rounded-3xl shadow-[0_10px_30px_rgba(79,80,72,0.05)] hover:shadow-[0_22px_55px_rgba(79,80,72,0.11)] transition-shadow duration-300 overflow-hidden"
      >
        <button
          data-testid={`faq-toggle-${index}`}
          onClick={onToggle}
          aria-expanded={open}
          className="w-full flex items-center gap-5 px-6 md:px-8 py-6 md:py-7 text-left"
        >
          <span className="font-serif text-lg md:text-xl text-kern-sage/70 w-6 md:w-8 shrink-0">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="flex-1 font-serif text-lg md:text-2xl leading-snug text-kern-dark">
            {item.q}
          </span>
          <span
            className={`w-10 h-10 rounded-full bg-kern-sage/25 flex items-center justify-center shrink-0 transition-transform duration-300 ${
              open ? "rotate-45 bg-kern-terracotta/25" : ""
            }`}
          >
            <Plus size={18} className="text-kern-olive" strokeWidth={1.8} />
          </span>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="px-6 md:px-8 pb-7 pl-[3.25rem] md:pl-[4.5rem] space-y-3 text-sm md:text-base text-kern-taupe leading-relaxed">
                {item.a.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
                {item.link && (
                  <button
                    data-testid={`faq-link-${index}`}
                    onClick={() => scrollToId(item.link.href)}
                    className="inline-flex items-center gap-2 mt-1 text-sm font-medium text-kern-terracotta hover:gap-3 transition-all duration-300"
                  >
                    {item.link.text}
                    <ArrowRight size={16} />
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
};

const LocationMap = () => {
  const { consent, accept } = useCookieConsent();
  return (
    <div className="mt-6 relative overflow-hidden rounded-[32px] shadow-[0_20px_60px_rgba(79,80,72,0.12)] border border-kern-taupe/15">
      {consent === "accepted" ? (
        <iframe
          title="Locatie Praktijk De Kern"
          data-testid="faq-map"
          src="https://maps.google.com/maps?q=E.%20Van%20Notenlaan%2025%2C%202960%20Sint-Lenaarts%2C%20Belgi%C3%AB&t=&z=15&ie=UTF8&iwloc=&output=embed"
          className="w-full h-[300px] md:h-[420px] border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      ) : (
        <div
          data-testid="faq-map-placeholder"
          className="w-full h-[300px] md:h-[420px] bg-kern-sage/15 flex flex-col items-center justify-center text-center px-6"
        >
          <span className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
            <MapPin size={20} className="text-kern-olive" strokeWidth={1.8} />
          </span>
          <p className="mt-4 max-w-xs text-sm text-kern-taupe leading-relaxed">
            Om de kaart te tonen laad ik Google Maps. Daarvoor heb ik je toestemming nodig.
          </p>
          <button
            data-testid="faq-map-load"
            onClick={accept}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-kern-terracotta text-kern-bg text-sm font-medium py-2.5 px-6 hover:bg-[#b98771] transition-colors duration-300"
          >
            Kaart tonen
          </button>
        </div>
      )}
    </div>
  );
};

export const Faq = () => {
  const [openIdx, setOpenIdx] = useState(-1);
  return (
  <section
    id="faq"
    data-testid="faq-page"
    className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-32"
  >
    <div className="pointer-events-none absolute -left-40 top-24 w-[520px] h-[520px] -z-10">
      <Motif opacity={0.14} />
    </div>
    <div className="pointer-events-none absolute right-[-6rem] bottom-[-4rem] w-[380px] h-[380px] -z-10">
      <Motif opacity={0.1} flip />
    </div>

    <div className="max-w-3xl mx-auto px-5 md:px-8">
      <Reveal>
        <SectionLabel className="mb-6">Info</SectionLabel>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-kern-dark">
          Veelgestelde vragen
        </h1>
        <p className="mt-6 max-w-xl text-base text-kern-taupe leading-relaxed">
          Hieronder vind je antwoorden op de vragen die ik het vaakst krijg.
          Staat jouw vraag er niet bij? Neem gerust contact met me op.
        </p>
      </Reveal>

      <div className="mt-12 md:mt-14 space-y-4">
        {FAQ_ITEMS.map((item, i) => (
          <FaqRow
            key={item.q}
            item={item}
            index={i}
            isOpen={openIdx === i}
            onToggle={() => setOpenIdx((prev) => (prev === i ? -1 : i))}
          />
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-14 md:mt-16">
          <SectionLabel className="mb-6">Locatie</SectionLabel>
          <h2 className="font-serif text-2xl md:text-4xl leading-snug text-kern-dark">
            Waar vind je me?
          </h2>
          <p className="mt-4 text-sm md:text-base text-kern-taupe leading-relaxed">
            E. Van Notenlaan 25, 2960 Sint-Lenaarts
          </p>
          <LocationMap />
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-14 md:mt-16 relative overflow-hidden rounded-[40px] bg-kern-olive px-7 md:px-12 py-10 md:py-14 text-center">
          <div className="pointer-events-none absolute -right-16 -bottom-20 w-[280px] h-[280px] opacity-30">
            <Motif opacity={0.5} flip />
          </div>
          <div className="relative">
            <h2 className="font-serif text-2xl md:text-4xl text-kern-bg leading-snug">
              Nog een vraag?
            </h2>
            <p className="mt-4 max-w-md mx-auto text-sm md:text-base text-kern-bg/80 leading-relaxed">
              Ik denk graag met je mee. Stuur me een bericht en ik neem zo snel
              mogelijk contact met je op.
            </p>
            <div className="mt-8 flex justify-center">
              <Button
                as="button"
                data-testid="faq-contact-cta"
                size="lg"
                variant="light"
                onClick={() => scrollToId("#contact")}
              >
                Neem contact op
              </Button>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
